/**
 * OverflowNav — a nav strip that gives up items it cannot fit
 * (Asana 1217649894859355 — dynamic formatting in the top menu).
 *
 * The Mission Control header hosts a page's own nav beside the breadcrumb, and
 * at half-screen widths the Fleet Editor's facet tabs had nowhere to go: they
 * painted over the account chip. This component measures the space it was given
 * and moves whatever no longer fits into a **More** disclosure, so every item
 * stays reachable and nothing overlaps.
 *
 * Five things here are less obvious than they look, and each one is a defect
 * that was found and closed rather than a preference:
 *
 * 1. **The mirrors are `span`s, never the live nodes.** Natural widths have to
 *    be measured somewhere, and the only honest place is a hidden copy of the
 *    row — the visible row is already the answer, not the question. Rendering
 *    the real `Link`s into that `aria-hidden` copy would give every facet a
 *    second, invisible tab stop, and focusable content inside `aria-hidden` is
 *    an axe `aria-hidden-focus` failure. The mirror therefore renders a `span`
 *    carrying the item's `className` and `label`, which is the same box for
 *    layout purposes and reaches neither the tab order nor a role query.
 * 2. **The measured root is block-level.** `AppShell` wraps the header slot in
 *    a `flex-1 min-w-[200px]` div — a zero basis, so the wrapper's width is the
 *    space left over rather than a function of how many items are currently
 *    shown. That invariant only survives if this component's own root fills the
 *    wrapper, so the root is `flex` (block-level). An `inline-flex` root would
 *    size to its content and reintroduce exactly the feedback loop the wrapper
 *    exists to prevent: hide one item, measure narrower, hide another, never
 *    recover on widening.
 * 3. **The More mirror is measured at an upper-bound label, not the real one.**
 *    The button says `More (3)`, and `More (12)` is wider — an input to the fit
 *    that is also its output. The mirror therefore renders the widest digit
 *    repeated to the digit count of the total (`More (88)` for ten to
 *    ninety-nine items), since a proportional font makes `More (19)` wider than
 *    `More (10)`. The rendered button is never wider than the space reserved.
 * 4. **The crowding publish uses the CURRENT chip state as an input.** The chip
 *    collapsing widens this strip, so a predicate written against the measured
 *    width alone would read a quantity its own outcome changed and flip forever.
 *    `isCrowded` cancels that by subtracting `reserve` while collapsed; this
 *    component's job is only to hand it the natural width, the measured width
 *    and both context values honestly. It also publishes `crowded: false` on
 *    unmount, so leaving the Fleet Editor cannot strand the chip as an avatar —
 *    the discipline `useHeaderSlot` already applies to the slot's content.
 * 5. **The panel is `position: fixed`.** The header row and its left group both
 *    clip (`overflow-hidden`) — that clip is what actually stops the overlap —
 *    and there is no `createPortal` anywhere in this codebase to copy. Fixed
 *    positioning is relative to the viewport and no ancestor here establishes a
 *    containing block via `transform`/`filter`/`contain`, so the panel escapes
 *    both clips. The cost is that it must be re-anchored on resize, clamped into
 *    the viewport, and closed when focus leaves the disclosure — a fixed panel
 *    has no ancestor doing any of that for it.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useHeaderCrowd } from '../context/HeaderCrowdContext'
import { useElementWidth } from '../hooks/useElementWidth'
import { computeFit, isCrowded, rowWidth, MORE_MIRROR_KEY } from './headerFit'

/** `gap-0.5` on the strip below, in px. The fit math needs the number. */
const GAP_PX = 2

/** `min-w-[10rem]` on the panel below, in px — the clamp needs the number. */
const PANEL_MIN_WIDTH_PX = 160

/** Gap kept between the panel and the viewport edge when the clamp bites. */
const VIEWPORT_MARGIN_PX = 8

/** Distance from the row's bottom edge to the top of the open panel, in px. */
const PANEL_OFFSET_PX = 4

/**
 * The button and its mirror must carry identical classes, or the reservation lies.
 *
 * The string is `FACET_TAB_CLASS` + `FACET_TAB_IDLE_CLASS` from
 * `FleetEditor.tsx:52-54` verbatim, so the More button reads as one more idle
 * facet tab beside the ones it is holding. That match is deliberate but NOT
 * enforced: this component is generic and must not import from a page, so the
 * two strings are kept in step by this citation and nothing else.
 */
const MORE_CLASS = 'px-2.5 py-1 rounded text-sm font-medium transition-colors text-gray-500 hover:text-gray-900 hover:bg-gray-100'

export interface OverflowNavItem {
  /** Stable identity, used for React keys and for the mirror's data attribute. */
  key: string
  /** What actually renders — in the strip, or inside the disclosure. */
  node: ReactNode
  /** Text of the mirror that stands in for `node` while measuring. */
  label: string
  /** At most one item is active. The active item is never hidden. */
  active?: boolean
  /**
   * The classes `node` carries, copied onto its mirror so the two boxes measure
   * the same. Without it the mirror loses the item's padding and font size and
   * every natural width comes back short, which under-hides and clips.
   */
  className?: string
}

export interface OverflowNavProps {
  items: OverflowNavItem[]
  /** Accessible name for the nav landmark. */
  ariaLabel: string
}

/**
 * The widest label the More button can ever render, as text.
 *
 * `count` is the total number of items, so the hidden count has at most as many
 * digits. The widest digit is used for every one of them.
 */
function upperBoundMoreLabel(count: number): string {
  // `String(0).length` is already 1, so no floor is needed on the digit count.
  return `More (${'8'.repeat(String(count).length)})`
}

interface Measured {
  /** Natural width of each item, in DOM order. */
  items: number[]
  /** Natural width of the More button at its upper-bound label. */
  more: number
}

function sameMeasurement(a: Measured | null, b: Measured): boolean {
  if (!a) return false
  if (a.more !== b.more || a.items.length !== b.items.length) return false
  return a.items.every((w, i) => w === b.items[i])
}

export function OverflowNav({ items, ariaLabel }: OverflowNavProps) {
  const rootRef = useRef<HTMLElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLUListElement>(null)
  const panelId = useId()

  /**
   * The space the strip has. `undefined` means "never measured", which
   * `computeFit` reads as "show everything" — distinct from a measured 0.
   */
  const available = useElementWidth(rootRef)
  const [measured, setMeasured] = useState<Measured | null>(null)

  // Read the mirrors after every commit that could have changed them: the item
  // list, or the width the row was given. Rounded, and bailing when unchanged,
  // so measure → publish → re-measure cannot loop on fractional rects.
  useLayoutEffect(() => {
    const row = mirrorRef.current
    if (!row) return
    const widths = new Map<string, number>()
    row.querySelectorAll<HTMLElement>('[data-mirror-key]').forEach(el => {
      widths.set(el.dataset.mirrorKey ?? '', Math.round(el.getBoundingClientRect().width))
    })
    const next: Measured = {
      items: items.map(item => widths.get(item.key) ?? 0),
      more: widths.get(MORE_MIRROR_KEY) ?? 0,
    }
    setMeasured(prev => (sameMeasurement(prev, next) ? prev : next))
  }, [items, available])

  // A measurement from a previous item list is not an answer about this one.
  const itemWidths = measured && measured.items.length === items.length
    ? measured.items
    : items.map(() => 0)
  const activeIndex = items.findIndex(item => item.active)
  const { visible, hidden } = computeFit({
    itemWidths,
    available,
    moreWidth: measured?.more ?? 0,
    activeIndex,
    gap: GAP_PX,
  })

  // Tell the shell whether the row is crowded enough to collapse the account
  // chip. `naturalWidth` is the strip's unshrunk row — every item plus the gaps
  // between them, and NOT the More button, since the question is whether the
  // items would fit without one.
  const { crowded, reserve, setCrowded } = useHeaderCrowd()
  const naturalWidth = rowWidth(itemWidths, GAP_PX)
  useEffect(() => {
    setCrowded(isCrowded({ naturalWidth, available, collapsed: crowded, reserve }))
  }, [naturalWidth, available, crowded, reserve, setCrowded])

  // Separate effect on purpose: folded into the one above, this cleanup would
  // fire on every measurement instead of only on unmount. Navigating from the
  // Fleet Editor to Health takes this strip with it, and the chip must not stay
  // an avatar on a page whose header has room to spare.
  useEffect(() => () => setCrowded(false), [setCrowded])

  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)

  const positionPanel = useCallback(() => {
    const button = buttonRef.current
    const rect = button?.getBoundingClientRect()
    if (!button || !rect) return
    // Below the ROW, not below the button: the button is a 28px control inside a
    // header with its own vertical padding, so `rect.bottom + 4` opens the panel
    // over that padding and the header's border. The host's bottom is used when
    // it is lower, which also keeps the button-relative fallback for a caller
    // with no header ancestor (and for jsdom, where every rect is zero).
    const host = button.closest('header')?.getBoundingClientRect()
    const top = Math.max(rect.bottom, host?.bottom ?? 0) + PANEL_OFFSET_PX
    // Clamped into the viewport: the button can sit close to the right edge, and
    // a panel anchored to its left would hang off-screen with nothing to scroll.
    const maxLeft = window.innerWidth - PANEL_MIN_WIDTH_PX - VIEWPORT_MARGIN_PX
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft))
    setAnchor({ top: Math.round(top), left: Math.round(left) })
  }, [])

  // Close whenever the split changes, so a resize cannot leave an empty panel
  // open or strand focus on a link that just moved back into the strip. Runs on
  // mount too, where closing is already the state.
  //
  // The signature joins on NUL so it cannot collide across item keys — no key
  // can contain one — and that separator must stay an ESCAPE rather than a
  // literal character: written literally it made git treat this source as binary.
  const hiddenSignature = hidden.map(i => items[i]?.key).join('\u0000')
  useEffect(() => {
    setOpen(false)
  }, [hiddenSignature])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    // Focus leaving the DISCLOSURE — the button and the panel together — closes
    // it, which is the keyboard half of the outside-click rule: without it,
    // tabbing past the last link leaves an open panel behind the caret with no
    // way to notice. A `null` relatedTarget (focus dropped to the document,
    // which is what choosing a link does) counts as leaving.
    //
    // "Together" is the part that is easy to get wrong. The button is a SIBLING
    // of the panel, not an ancestor, so a listener bound to the panel alone
    // never sees the button's own blur: Shift+Tab off an open More button moves
    // focus back into the strip and used to leave the panel open with focus
    // outside it. The two moves INSIDE the disclosure — button → panel on Tab,
    // panel → button on Shift+Tab — are the reason the `relatedTarget` test
    // below names both elements rather than the listener's own host.
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null
      if (next && (panelRef.current?.contains(next) || buttonRef.current?.contains(next))) return
      setOpen(false)
    }
    // Resize only. Deliberately NOT scroll: the header does not scroll, `<main>`
    // does, so a window-level scroll listener would fire on unrelated content
    // scrolling and on the panel's own. The anchor is viewport-fixed, so an open
    // panel stays put and correct.
    const onResize = () => positionPanel()
    // Bound to this nav's own root, never the document: a document-level
    // `focusout` fires for every blur on the page. The root is the nearest
    // element that contains BOTH the button and the panel — the panel is
    // `position: fixed`, which changes where it paints and not where it sits in
    // the tree, so it is still a descendant here — and `focusout` bubbles, so
    // one listener covers both.
    const root = rootRef.current
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('resize', onResize)
    root?.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('resize', onResize)
      root?.removeEventListener('focusout', onFocusOut)
    }
  }, [open, positionPanel])

  return (
    <nav
      ref={rootRef}
      aria-label={ariaLabel}
      className="relative flex items-center gap-0.5 whitespace-nowrap"
    >
      {/* The measuring row. Out of flow, so it cannot affect the width being
          measured; `invisible` (not `hidden`) keeps it measurable; `aria-hidden`
          plus plain `span`s keep it out of both the tab order and every role
          query.

          `whitespace-nowrap` sits on the root now, so the live row and this one
          are measured under the same rule, and it is what makes this row honest.
          The root is `relative`, so the mirror
          measures inside a containing block only ~200px wide — narrower than the
          row it stands for. Nothing shrinks anyway: the row never wraps
          (`flex-wrap` is not set), and `nowrap` raises each span's min-content to
          its max-content, which is the automatic minimum size a flex item cannot
          shrink below. So the spans overflow at their natural width instead of
          reporting a short one, which is what would under-hide and let the real
          row clip. A `min-w-0`, a `flex-wrap`, or an `overflow-hidden` ON THIS
          ROW would each undo that.

          The `absolute inset-0 overflow-hidden` box is what keeps that overflow
          local. Without it the row's ~90px of overhang is permanent horizontal
          SCROLLABLE overflow on the header's two `overflow-hidden` boxes, and an
          `overflow:hidden` box is still programmatically scrollable — one
          `scrollIntoView` or focus-reveal could displace the whole header row
          with no way back. Clipping never affects `getBoundingClientRect`, so the
          measurement is unchanged. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          ref={mirrorRef}
          aria-hidden="true"
          className="flex items-center gap-0.5 invisible pointer-events-none whitespace-nowrap"
        >
          {items.map(item => (
            <span key={item.key} data-mirror-key={item.key} className={item.className}>
              {item.label}
            </span>
          ))}
          <span data-mirror-key={MORE_MIRROR_KEY} className={MORE_CLASS}>
            {upperBoundMoreLabel(items.length)}
          </span>
        </div>
      </div>

      {visible.map(index => (
        <Fragment key={items[index].key}>{items[index].node}</Fragment>
      ))}

      {/* No button at all when nothing is hidden: `hidden.length > 0` is the
          only signal the fit carries about it. The label shows the REAL count;
          the width reserved for it was the upper bound. */}
      {hidden.length > 0 && (
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            positionPanel()
            setOpen(prev => !prev)
          }}
          className={`shrink-0 ${MORE_CLASS}`}
        >
          More ({hidden.length})
        </button>
      )}

      {/* A disclosure, not a menu widget: no `role="menu"`, no `menuitem`, no
          arrow-key roving. These are navigation links, and a menu role would
          suppress the "link" announcement assistive technology should give
          them. Tab order simply flows through the links.

          `fixed` and `z-50`, never `absolute`: the header row and its left
          group both clip, and the panel creates no stacking context of its own
          while `z-20` sticky headers already exist on Fleet Editor surfaces
          (`DataSetForm.tsx:512`). */}
      {open && hidden.length > 0 && (
        <ul
          ref={panelRef}
          id={panelId}
          style={{ top: anchor?.top ?? 0, left: anchor?.left ?? 0 }}
          className="fixed z-50 min-w-[10rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          onClick={() => setOpen(false)}
        >
          {/* `flex-col`, so the item stretches across the row. A row-direction
              flex parent leaves the link at its content width, which makes the
              rest of the row — the widest label's worth of it, plus the panel's
              own padding — look clickable without being clickable. Stretching is
              done here rather than by a class on the item, since `node` is an
              opaque ReactNode this component does not style. */}
          {hidden.map(index => (
            <li key={items[index].key} className="flex flex-col px-1 py-0.5">
              {items[index].node}
            </li>
          ))}
        </ul>
      )}
    </nav>
  )
}
